/**
 * ReignMark — the Elreign "E" letterform logo in SVG.
 * Ported verbatim from components.jsx in the Claude Design output.
 *
 * WALDO 2026-08-05 — "on the registration page the logo isn't crisp, there are
 * white pixels or artifacts hovering over it."
 *
 * ROOT CAUSE (measured on the live /auth/signup page, not inferred). The mark
 * used to be FOUR overlapping <rect>s — a 72-unit stem plus three bars — and all
 * four started at the same x=20. The viewBox is 100 units wide rendered at 30–32
 * CSS px, so that shared left edge lands on x = 6.4px: a fractional boundary the
 * renderer has to antialias. Each rect is rasterised and composited
 * INDEPENDENTLY (source-over), so along that one straight line the alpha
 * alternates with how many rects happen to overlap there:
 *
 *     y  5– 8px  α 214   stem + top bar
 *     y  9–13px  α 153   stem alone      ← 24% lighter
 *     y 14–17px  α 214   stem + mid bar
 *     y 19–22px  α 153   stem alone      ← 24% lighter
 *     y 23–26px  α 214   stem + bottom bar
 *
 * i.e. the left edge of the "E" was banded light/dark/light/dark down its own
 * height. Against the cream page that reads as pale speckle sitting on the mark
 * — exactly what Waldo saw. The `rx="1"` corners made it worse for nothing: at
 * that scale a 1-unit radius is 0.32px, pure extra antialiasing and no visible
 * rounding.
 *
 * FIX: the same silhouette drawn as ONE path, so there is ONE antialiased
 * outline and nothing to double-composite. No asset change, no raster, no
 * re-export — the geometry is identical to the union of the four rects
 * (stem x20–34 y14–86 · top x20–84 y14–28 · mid x20–62 y44–58 · bottom
 * x20–78 y72–86). The brass dot is a separate shape that never touched the
 * letterform (it starts at x=80, the bottom bar ends at x=78), so it stays.
 *
 * `shapeRendering="geometricPrecision"` keeps the outline smooth rather than
 * letting a UA snap it to the pixel grid at small sizes.
 */

interface ReignMarkProps {
  size?: number;
}

export function ReignMark({ size = 30 }: ReignMarkProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      aria-hidden="true"
      focusable="false"
      shapeRendering="geometricPrecision"
    >
      {/* STABLE HOOKS — do not remove. Surfaces that need to recolour the mark
          (the dark-teal marketing hero) target these class names, NOT the
          element type. The element type has now broken that override TWICE:
          once when the CSS keyed off `.reign-mark` (a class the header SVG never
          rendered), and again on 2026-08-05 when the crispness fix above merged
          four <rect>s into one <path> and the `svg rect` selector silently
          stopped matching — leaving a dark-teal E on a dark-teal hero at about
          1:1 contrast, invisible, while the accent dot kept working because its
          `circle` selector still matched. Changing the geometry is fine; keep
          these hooks attached to whatever draws the letterform and the dot. */}
      <path
        className="reign-mark__letter"
        d="M20 14 H84 V28 H34 V44 H62 V58 H34 V72 H78 V86 H20 Z"
        fill="var(--primary-700)"
      />
      <circle className="reign-mark__dot" cx="86" cy="79" r="6" fill="var(--accent-500)" />
    </svg>
  );
}
